import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { Observable, catchError, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Known Mass Mailing functional domains. Hand-maintained: there is no reference-data
 * endpoint for "which functional domains exist" (AZTGSA-12354 design.md, Decision 1).
 * Mirrors AZTGSA-10897's own notificationTypeCode -> functionalDomainCode mapping —
 * extend both together when a new domain onboards. Only `TCI` (Compass) is live today.
 */
export const KNOWN_MASS_MAILING_DOMAINS: readonly string[] = ['TCI'];

const ACTION = 'gsa_front_mass-mailing-campaign_manager';
const RESOURCE_PREFIX = 'GSA_MASS-MAILING';
const ALL_RESOURCE = `${RESOURCE_PREFIX}_ALL`;

export interface AuthorizedBusinessUnit {
  businessUnitCode: string;
  businessUnitLabel: string;
  contentLanguage: string;
  defaultLanguageCode: string;
  countryCodes: string[];
  domains: string[];
}

export type AuthorizationStep = 'loading-business-units' | 'checking-permissions';

export type AuthorizationResolution =
  | { status: 'authorized'; businessUnits: AuthorizedBusinessUnit[] }
  | { status: 'zero-access' }
  | { status: 'unknown' };

interface EnrichedBusinessUnit {
  businessUnitCode?: string;
  businessUnitLabel?: string;
  contentLanguage?: string;
  defaultLanguageCode?: string;
  countryCodes?: string[];
}

interface PermissionsCheckResponse {
  resourcesChecks?: { resource: string; hasAccess: boolean }[];
}

interface CacheEntry {
  gadId: string;
  cachedAt: number;
  resolution: AuthorizationResolution;
}

export const CAMPAIGN_AUTHORIZATION_CACHE_KEY = 'mass-mailing-portal:campaignAuthorizationCache';
const CACHE_KEY = CAMPAIGN_AUTHORIZATION_CACHE_KEY;
const CACHE_TTL_MS = 60 * 60 * 1000;

function domainResource(domain: string): string {
  return `${RESOURCE_PREFIX}_${domain}`;
}

function leafResource(domain: string, businessUnitCode: string): string {
  return `${RESOURCE_PREFIX}_${domain}_${businessUnitCode}`;
}

/**
 * Every `GSA_MASS-MAILING_ALL` / `GSA_MASS-MAILING_<domain>` / `GSA_MASS-MAILING_<domain>_<code>`
 * resource to check for the given Business Unit codes, deduplicated (the permissions/check API
 * echoes duplicates rather than deduping them itself). `domains` defaults to the known-domains
 * constant but is overridable so resolution logic can be exercised with more than one domain
 * in tests, ahead of a second domain actually being onboarded.
 */
export function buildCandidateResources(
  businessUnitCodes: readonly string[],
  domains: readonly string[] = KNOWN_MASS_MAILING_DOMAINS,
): string[] {
  const resources = new Set<string>([ALL_RESOURCE]);
  for (const domain of domains) {
    resources.add(domainResource(domain));
    for (const code of businessUnitCodes) {
      resources.add(leafResource(domain, code));
    }
  }
  return [...resources];
}

/**
 * Three-tier resolution (AZTGSA-12354 design.md, Decision 3): an org-wide `ALL` grant
 * authorizes every Business Unit under every domain; a domain-wide grant authorizes every
 * Business Unit under that one domain; otherwise a Business Unit is authorized under a domain
 * only via its own leaf grant. A Business Unit's authorized-domains list is the union of
 * whichever tiers apply, so it can end up authorized under more than one domain.
 */
export function resolveAuthorizedBusinessUnits(
  businessUnits: EnrichedBusinessUnit[],
  access: ReadonlyMap<string, boolean>,
  domains: readonly string[] = KNOWN_MASS_MAILING_DOMAINS,
): AuthorizedBusinessUnit[] {
  const orgWide = access.get(ALL_RESOURCE) === true;
  const domainWide = new Set(domains.filter((domain) => access.get(domainResource(domain)) === true));

  const authorized: AuthorizedBusinessUnit[] = [];
  for (const businessUnit of businessUnits) {
    const code = businessUnit.businessUnitCode;
    if (!code) {
      continue;
    }

    const authorizedDomains = domains.filter(
      (domain) => orgWide || domainWide.has(domain) || access.get(leafResource(domain, code)) === true,
    );
    if (authorizedDomains.length === 0) {
      continue;
    }

    authorized.push({
      businessUnitCode: code,
      businessUnitLabel: businessUnit.businessUnitLabel ?? '',
      contentLanguage: businessUnit.contentLanguage ?? '',
      defaultLanguageCode: businessUnit.defaultLanguageCode ?? '',
      countryCodes: businessUnit.countryCodes ?? [],
      domains: authorizedDomains,
    });
  }
  return authorized;
}

function isCacheEntry(value: unknown): value is CacheEntry {
  const entry = value as Partial<CacheEntry> | null;
  if (!entry || typeof entry.gadId !== 'string' || typeof entry.cachedAt !== 'number' || !entry.resolution) {
    return false;
  }

  const resolution = entry.resolution as { status?: unknown; businessUnits?: unknown };
  if (resolution.status === 'zero-access') {
    return true;
  }
  return resolution.status === 'authorized' && Array.isArray(resolution.businessUnits);
}

@Injectable({ providedIn: 'root' })
export class CampaignAuthorizationService {
  private readonly http = inject(HttpClient);
  private readonly oauthService = inject(OAuthService);

  private readonly _currentStep = signal<AuthorizationStep | undefined>(undefined);
  /** The step of a live resolution currently in flight; `undefined` outside of one (including on a cache hit). */
  readonly currentStep = this._currentStep.asReadonly();

  /**
   * Resolves whether the authenticated user is authorized to manage at least one Mass
   * Mailing Business Unit, and if so, the enriched list of authorized Business Units with
   * the functional domain(s) each is authorized under. Cached client-side for 1h, keyed by
   * the user's identity (AZTGSA-12354 design.md, Decision 7).
   */
  resolve(): Observable<AuthorizationResolution> {
    const gadId = this.getGadId();
    const cached = this.readCache();
    if (cached?.gadId === gadId && Date.now() - cached.cachedAt < CACHE_TTL_MS) {
      return of(cached.resolution);
    }

    this._currentStep.set('loading-business-units');
    return this.fetchBusinessUnits().pipe(
      switchMap((businessUnits) => {
        this._currentStep.set('checking-permissions');
        const codes = businessUnits
          .map((businessUnit) => businessUnit.businessUnitCode)
          .filter((code): code is string => !!code);

        return this.checkPermissions(buildCandidateResources(codes)).pipe(
          map((access) => this.toResolution(resolveAuthorizedBusinessUnits(businessUnits, access))),
        );
      }),
      catchError((error: unknown) => of(this.classifyError(error))),
      tap((resolution) => {
        this._currentStep.set(undefined);
        if (resolution.status !== 'unknown') {
          this.writeCache({ gadId, cachedAt: Date.now(), resolution });
        }
      }),
    );
  }

  private fetchBusinessUnits(): Observable<EnrichedBusinessUnit[]> {
    const url = `${environment.referenceDataApiUrl}/businessUnits?includeFields=countryCodes,defaultLanguageCode`;
    return this.http.get<EnrichedBusinessUnit[]>(url);
  }

  private checkPermissions(resources: string[]): Observable<Map<string, boolean>> {
    const url = `${environment.permissionsApiUrl}/permissions/check`;
    return this.http.post<PermissionsCheckResponse>(url, { action: ACTION, resources }).pipe(
      map((response) => new Map((response.resourcesChecks ?? []).map((check) => [check.resource, check.hasAccess] as const))),
    );
  }

  private toResolution(businessUnits: AuthorizedBusinessUnit[]): AuthorizationResolution {
    return businessUnits.length > 0 ? { status: 'authorized', businessUnits } : { status: 'zero-access' };
  }

  /**
   * A `403 AUTHORIZATION_ERROR` is UM's "every requested resource denied" signal
   * (design.md, Decision 4) — treated as a confirmed zero-access result. Any other error
   * (network failure, a different error body, an unexpected shape) fails open: the front's
   * check is a UX convenience, not the security boundary the API independently re-enforces.
   */
  private classifyError(error: unknown): AuthorizationResolution {
    if (error instanceof HttpErrorResponse && error.status === 403) {
      const body = error.error as { errorCode?: string } | null;
      if (body?.errorCode === 'AUTHORIZATION_ERROR') {
        return { status: 'zero-access' };
      }
    }
    return { status: 'unknown' };
  }

  private readCache(): CacheEntry | undefined {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) {
        return undefined;
      }

      const parsed = JSON.parse(raw);
      return isCacheEntry(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }

  private writeCache(entry: CacheEntry): void {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
      // localStorage may be unavailable (private mode, quota exceeded, ...) — caching is best-effort.
    }
  }

  private getGadId(): string {
    const claims = this.oauthService.getIdentityClaims() as { sub?: string; external_id?: string } | null;
    const rawId = claims?.sub ?? claims?.external_id ?? '';
    return rawId.replace(/^GAD\\/, '');
  }
}
